import { useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import SpinningLoader from "../components/common/SpinningLoader";
import {
  ApiError,
  create,
  getQuery,
  setAuthorization,
} from "../services/apiService";
import { performLogout } from "../services/logoutService";
import {
  getAuthToken,
  setAuthToken,
  setAuthData,
  clearAuthData,
  isTokenValid,
} from "./AuthActions";
import { consumePostAuthRedirect } from "../utils/authRedirect";
import { ensurePersonalWalletOnLogin } from "../utils/ensurePersonalWalletOnLogin";
import { DISPLAY_CURRENCY_CHANGE_EVENT } from "./CurrencySelectionContext";
import {
  normalizeDisplayCurrency,
  type DisplayCurrency,
} from "../utils/displayCurrency";
import { useActiveBusiness } from "../hooks/query/dposQueries";
import {
  useBusinessMe,
  isBusinessTwoFactorEnabled,
  getBusinessMeLocalCurrency,
} from "../hooks/query/businessQueries";
import { useWalletScope } from "./WalletScopeContext";

interface UserSettings {
  security?: {
    twoFactorAuth?: { enabled?: boolean };
    twoFaLogin?: boolean;
    autoLogoutDuration?: string;
    antiPhishCode?: { enabled?: boolean; phrase?: string };
    lockMethod?: { enabled?: boolean; method?: string };
    pinSet?: boolean;
  };
  notifications?: {
    allowPushNotification?: boolean;
    accountActivity?: boolean;
    priceAlerts?: boolean;
    productAnnouncements?: boolean;
  };
  preferences?: {
    localCurrency?: string;
    performancePeriod?: string;
    language?: string;
    showBalances?: boolean;
    hideZeroBalances?: boolean;
  };
  digitalWallet?: {
    showBalances?: boolean;
    hideZeroBalances?: boolean;
  };
  [key: string]: unknown;
}

interface UserProfile {
  _id: string;
  authId: string;
  email: string;
  emailVerified: boolean;
  username: string;
  role?: number;
  personal_info?: {
    name?: string;
    lastname?: string;
    legal_name?: string;
    legal_lastname?: string;
    identity_number?: string;
    title?: string;
    dob?: string;
    phoneNumber?: string;
  };
  phone_number?: {
    country_code?: string;
    number?: string;
  };
  address?: {
    street?: string;
    suburb?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    fullAddress?: string;
    country?: string;
    address?: string;
  };
  accountType: string;
  accountId?: string;
  kycStatus: string;
  notificationPreferences?: Record<string, boolean>;
  preferences?: {
    localCurrency?: string;
    performancePeriod?: string;
    showBalances?: boolean;
    hideZeroBalances?: boolean;
  };
  verificationDetails?: { status?: string };
  ref_info?: { user_code?: string };
  sub?: string;
  status: string;
  isBlocked: boolean;
  profile_completed?: boolean;
  twoFactorAuth?: boolean;
  settings?: UserSettings;
  metadata?: Record<string, unknown>;
  subAccountDetails?: Array<{ _id: string; [key: string]: unknown }>;
  pendingCreditsReceived?: PendingCreditReceived[];
  monthly_salary_range?: string;
  marketing_consent?: boolean;
  terms_conditions?: boolean;
  employmentStatus?: string;
  sourceOfFunds?: string;
  monthlySalaryRange?: string;
  funds_source?: string;
  countryAcceptance?: string;
  [key: string]: unknown;
}

interface PendingCreditReceived {
  paymentId: string;
  transactionId: string;
  amount: string;
  currency: string;
  fromSender: string;
  receivedAt: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  userProfile: UserProfile | null;
  user: UserProfile | null;
  settings: UserSettings | null;
  loading: boolean;
  isLoggingOut: boolean;
  signInUser: (
    email: string,
    password: string
  ) => Promise<UserProfile | { requiresTwoFA: boolean }>;
  refreshUser: (
    tokenParam?: string | null,
    options?: { force?: boolean }
  ) => Promise<UserProfile | null>;
  handleLogout: () => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updatedProfile: Partial<UserProfile>) => void;
  updateSettings: (updatedSettings: UserSettings) => void;
  fetchUserSettings: (
    userData?: UserProfile | null,
    tokenParam?: string | null
  ) => Promise<void>;
  getUser: (tokenParam?: string | null) => Promise<UserProfile | null>;
  getAccountDetails: (
    tokenParam?: string | null
  ) => Promise<UserProfile | null>;
  currentUser: UserProfile | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function syncDisplayCurrencyPreference(settings?: UserSettings | null): void {
  const localCurrency = settings?.preferences?.localCurrency;
  if (typeof window === "undefined" || !localCurrency) {
    return;
  }

  const displayCurrency = normalizeDisplayCurrency(localCurrency);
  window.sessionStorage.setItem("80eight.displayCurrency", displayCurrency);
  window.dispatchEvent(
    new CustomEvent(DISPLAY_CURRENCY_CHANGE_EVENT, { detail: displayCurrency })
  );
}

function mergeUserSettings(
  profileSettings: UserSettings | null | undefined,
  securitySettings: UserSettings | null | undefined,
  preferences: UserSettings["preferences"] | null | undefined
): UserSettings | null {
  if (!profileSettings && !securitySettings && !preferences) {
    return null;
  }

  const mergedPreferences =
    profileSettings?.preferences || securitySettings?.preferences || preferences
      ? {
          ...profileSettings?.preferences,
          ...securitySettings?.preferences,
          ...preferences,
        }
      : undefined;

  return {
    ...(profileSettings ?? {}),
    ...(securitySettings ?? {}),
    ...(mergedPreferences ? { preferences: mergedPreferences } : {}),
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navigate = useNavigate();

  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0();

  const initialLoadRef = useRef(false);
  const getUserPromiseRef = useRef<Promise<UserProfile | null> | null>(null);
  const profileRequestVersionRef = useRef(0);
  const fetchUserSettingsPromiseRef =
    useRef<Promise<UserProfile | null> | null>(null);
  const socialAuthProcessedRef = useRef(false);
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAuthenticated = useMemo(() => {
    if (isLoggingOut) return false;
    if (userProfile) {
      const token = getAuthToken();
      return isTokenValid(token);
    }
    return false;
  }, [userProfile, isLoggingOut]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    try {
      setUserProfile(null);
      setSettings(null);

      socialAuthProcessedRef.current = false;
      getUserPromiseRef.current = null;
      fetchUserSettingsPromiseRef.current = null;

      await new Promise((resolve) => setTimeout(resolve, 100));

      await performLogout(auth0Logout, `${window.location.origin}/landing`);

      logoutTimeoutRef.current = setTimeout(() => {
        navigate("/landing", { replace: true });
        setIsLoggingOut(false);
      }, 500);
    } catch (error) {
      console.error("Logout error:", error);
      setIsLoggingOut(false);
      navigate("/landing", { replace: true });
    }
  }, [auth0Logout, navigate]);

  useEffect(() => {
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, []);

  const fetchUserFromAPI = useCallback(
    async (
      tokenParam: string | null = null,
      options: { allowUnauthenticated?: boolean } = {}
    ): Promise<UserProfile | null> => {
      if (isLoggingOut) return null;

      const { allowUnauthenticated = false } = options;

      try {
        const token = tokenParam || getAuthToken();
        if (!token || !isTokenValid(token)) {
          return null;
        }

        setAuthorization(token);

        const userData = await getQuery<UserProfile>("/api/v2/user/me");

        if (userData?.isBlocked === true) {
          if (!isLoggingOut) {
            clearAuthData();
            sessionStorage.clear();
            setAuthorization(null);
            setUserProfile(null);
            setSettings(null);
            navigate("/account/blocked", { replace: true });
          }
          return null;
        }

        return userData || null;
      } catch (error) {
        console.error("Error fetching user from API:", error);

        const pendingId = localStorage.getItem("pendingId");
        const shouldLogout =
          error instanceof ApiError &&
          (error.statusCode === 401 || error.statusCode === 403);

        if (
          !isLoggingOut &&
          !pendingId &&
          shouldLogout &&
          !allowUnauthenticated
        ) {
          handleLogout();
        }
        return null;
      }
    },
    [handleLogout, isLoggingOut, navigate]
  );

  const getUser = useCallback(
    async (tokenParam: string | null = null): Promise<UserProfile | null> => {
      if (getUserPromiseRef.current) return getUserPromiseRef.current;

      const requestVersion = ++profileRequestVersionRef.current;
      const promise = fetchUserFromAPI(tokenParam)
        .then((data) => {
          if (data && requestVersion === profileRequestVersionRef.current) {
            setUserProfile(data);
          }
          return data;
        })
        .finally(() => {
          getUserPromiseRef.current = null;
        });

      getUserPromiseRef.current = promise;
      return promise;
    },
    [fetchUserFromAPI]
  );

  const refreshUser = useCallback(
    async (
      tokenParam: string | null = null,
      options: { force?: boolean } = {}
    ): Promise<UserProfile | null> => {
      if (getUserPromiseRef.current && !options.force) {
        return getUserPromiseRef.current;
      }

      try {
        const requestVersion = ++profileRequestVersionRef.current;
        const promise = fetchUserFromAPI(tokenParam);
        getUserPromiseRef.current = promise;

        const data = await promise;
        const isLatestRequest =
          requestVersion === profileRequestVersionRef.current;
        if (data) {
          if (data.isBlocked === true) {
            if (!isLoggingOut && isLatestRequest) {
              clearAuthData();
              sessionStorage.clear();
              setAuthorization(null);
              setUserProfile(null);
              setSettings(null);
              navigate("/account/blocked", { replace: true });
            }
            return null;
          }

          if (isLatestRequest) {
            setUserProfile(data);
            if (data.profile_completed) {
              const freshSettings = await getQuery<UserSettings>(
                "/api/v2/settings"
              ).catch((error: unknown) => {
                console.error("Could not get security settings:", error);
                return null;
              });
              const nextSettings = mergeUserSettings(
                data.settings as UserSettings | undefined,
                freshSettings,
                data.preferences
              );
              if (nextSettings) {
                setSettings(nextSettings);
                syncDisplayCurrencyPreference(nextSettings);
              }
            }

            const token = tokenParam || getAuthToken();
            if (token) {
              const session = {
                aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
                email: data.email,
                emailVerified: data.emailVerified,
                name: `${data.personal_info?.name || ""} ${data.personal_info?.lastname || ""}`.trim(),
                updatedAt: (data as Record<string, unknown>).updatedAt,
                sub: data.authId,
              };

              setAuthData({
                token,
                userId: data.authId,
                email: data.email,
                session,
                accountId: data.accountId,
              });
            }
          }

          return data;
        }
        return null;
      } catch (error) {
        console.error("Error in refreshUser:", error);
        return null;
      } finally {
        getUserPromiseRef.current = null;
      }
    },
    [fetchUserFromAPI, isLoggingOut, navigate]
  );

  const signInUser = useCallback(
    async (email: string, password: string) => {
      try {
        clearAuthData();
        sessionStorage.clear();

        setUserProfile(null);
        setSettings(null);

        setAuthorization(null);

        const signInData = await create<{
          user?: UserProfile;
          accessToken?: string;
          token?: string;
          access_token?: string;
          requiresTwoFA?: boolean;
          pendingId?: string;
        }>("/api/v2/auth/sign-in", { email, password });

        if ((signInData as Record<string, unknown>).requiresTwoFA) {
          localStorage.setItem(
            "pendingId",
            (signInData as Record<string, unknown>).pendingId as string
          );
          return {
            requiresTwoFA: true,
          };
        }

        const accessToken =
          signInData?.accessToken ||
          signInData?.token ||
          signInData?.access_token ||
          getAuthToken();

        if (!accessToken) {
          throw new Error("Sign-in succeeded but no access token was returned");
        }

        setAuthToken(accessToken);
        setAuthorization(accessToken);

        const userData = await getQuery<UserProfile>("/api/v2/user/me");

        if (userData) {
          if (userData.isBlocked === true) {
            clearAuthData();
            sessionStorage.clear();
            setAuthorization(null);
            setUserProfile(null);
            setSettings(null);
            navigate("/account/blocked", { replace: true });
            const blockedError = new Error("USER_BLOCKED") as Error & {
              isBlocked: boolean;
            };
            blockedError.isBlocked = true;
            throw blockedError;
          }

          const session = {
            aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
            email: userData.email,
            emailVerified: userData.emailVerified,
            name: `${userData.personal_info?.name || ""} ${userData.personal_info?.lastname || ""}`.trim(),
            updatedAt: (userData as Record<string, unknown>).updatedAt,
            sub: userData.authId,
          };

          setAuthData({
            token: accessToken || undefined,
            userId: userData.authId,
            email: userData.email,
            session,
            accountId: userData.accountId,
          });

          setUserProfile(userData);

          if (userData.profile_completed) {
            const freshSettings = await getQuery<UserSettings>(
              "/api/v2/settings"
            ).catch((error: unknown) => {
              console.error("Could not get security settings:", error);
              return null;
            });
            const nextSettings = mergeUserSettings(
              userData.settings as UserSettings | undefined,
              freshSettings,
              userData.preferences
            );
            if (nextSettings) {
              setSettings(nextSettings);
              syncDisplayCurrencyPreference(nextSettings);
            }
          }

          if (!userData.emailVerified) {
            navigate("/onboarding/verify-email", { replace: true });
          } else if (!userData.profile_completed) {
            const storedEmails = JSON.parse(
              localStorage.getItem("verifiedEmails") || "[]"
            ) as string[];
            const emailVerifiedFromStorage = storedEmails.includes(
              userData.email
            );
            if (emailVerifiedFromStorage) {
              navigate("/complete-profile", { replace: true });
            } else {
              navigate("/onboarding/welcome", { replace: true });
            }
          } else {
            await ensurePersonalWalletOnLogin();
            navigate(consumePostAuthRedirect(), { replace: true });
          }

          return userData;
        }

        throw new Error("Failed to fetch user data");
      } catch (error: unknown) {
        console.error("Sign in error:", error);

        const apiError = error as ApiError & {
          isBlocked?: boolean;
        };

        const envelope = apiError.response;
        const serverMessage = envelope?.message;

        if (
          envelope?.status === false &&
          (serverMessage === "Account is blocked. Please contact support." ||
            serverMessage === "user is blocked")
        ) {
          const blockedError = new Error("USER_BLOCKED") as Error & {
            isBlocked: boolean;
          };
          blockedError.isBlocked = true;
          throw blockedError;
        }

        if (apiError.message === "USER_BLOCKED" || apiError.isBlocked) {
          throw error;
        }

        throw new Error(
          serverMessage || apiError.message || "Failed to log in"
        );
      }
    },
    [navigate]
  );

  const fetchUserSettings = useCallback(
    async (
      userData: UserProfile | null = null,
      tokenParam: string | null = null
    ) => {
      try {
        let targetUserData = userData || userProfile;
        const token = tokenParam || getAuthToken();

        if (!targetUserData) {
          if (!token) return;

          if (fetchUserSettingsPromiseRef.current) {
            const data = await fetchUserSettingsPromiseRef.current;
            const freshSettings = await getQuery<UserSettings>(
              "/api/v2/settings"
            ).catch(() => null);
            const nextSettings = mergeUserSettings(
              data?.settings as UserSettings | undefined,
              freshSettings,
              data?.preferences
            );
            if (nextSettings) {
              setSettings(nextSettings);
              syncDisplayCurrencyPreference(nextSettings);
            }
            if (data) setUserProfile(data);
            return;
          }

          setAuthorization(token);
          const promise: Promise<UserProfile | null> = getQuery<UserProfile>(
            "/api/v2/user/me"
          )
            .then((data) => data || null)
            .catch(() => null)
            .finally(() => {
              fetchUserSettingsPromiseRef.current = null;
            });

          fetchUserSettingsPromiseRef.current = promise;
          targetUserData = await promise;
          if (targetUserData) {
            setUserProfile(targetUserData);
          }
        }

        if (!targetUserData?.profile_completed) {
          return;
        }

        if (token) {
          setAuthorization(token);
        }

        const freshSettings = await getQuery<UserSettings>(
          "/api/v2/settings"
        ).catch((error: unknown) => {
          console.error("Could not get security settings:", error);
          return null;
        });
        const nextSettings = mergeUserSettings(
          targetUserData.settings as UserSettings | undefined,
          freshSettings,
          targetUserData.preferences
        );

        if (nextSettings) {
          setSettings(nextSettings);
          syncDisplayCurrencyPreference(nextSettings);
        }
      } catch (err) {
        console.error("Could not get account settings:", err);
        fetchUserSettingsPromiseRef.current = null;
      }
    },
    [userProfile]
  );

  const updateSettings = useCallback((updatedSettings: UserSettings) => {
    setSettings((prevSettings) => {
      const nextSettings = {
        ...prevSettings,
        ...updatedSettings,
        preferences:
          prevSettings?.preferences || updatedSettings.preferences
            ? {
                ...prevSettings?.preferences,
                ...updatedSettings.preferences,
              }
            : undefined,
      };
      syncDisplayCurrencyPreference(nextSettings);
      return nextSettings;
    });
  }, []);

  const updateProfile = useCallback((updatedProfile: Partial<UserProfile>) => {
    setUserProfile((prevProfile) => {
      if (prevProfile) {
        return { ...prevProfile, ...updatedProfile };
      }

      const incomingRecord = updatedProfile as Record<string, unknown>;
      const looksLikeFullProfile =
        typeof incomingRecord.email === "string" ||
        typeof incomingRecord._id === "string" ||
        typeof incomingRecord.authId === "string" ||
        typeof incomingRecord.userId === "string";

      if (looksLikeFullProfile) {
        return updatedProfile as UserProfile;
      }

      return null;
    });
  }, []);

  const handleSocialAuthCallback = useCallback(async () => {
    clearAuthData();
    sessionStorage.clear();

    setUserProfile(null);
    setSettings(null);

    setAuthorization(null);

    if (!auth0IsAuthenticated || !auth0User || socialAuthProcessedRef.current) {
      return;
    }

    try {
      socialAuthProcessedRef.current = true;

      const auth0Token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH_AUDIENCE,
          scope: "openid profile email",
        },
      });

      setAuthToken(auth0Token);
      setAuthorization(auth0Token);

      const isEmailAlreadyRegistered = (error: unknown): boolean => {
        if (!(error instanceof ApiError)) return false;
        if (error.statusCode === 409) {
          return true;
        }
        if (error.statusCode !== 400) {
          return false;
        }
        const msg = (error.message || "").toLowerCase();
        return (
          msg.includes("already") ||
          msg.includes("exist") ||
          msg.includes("registered") ||
          msg.includes("taken")
        );
      };

      const ensureBackendUser = async () => {
        if (!auth0User.email) {
          throw new Error("Auth0 signup completed but email is missing");
        }

        try {
          await create("/api/v2/auth/sign-up", {
            email: auth0User.email,
            username:
              typeof auth0User.nickname === "string" && auth0User.nickname
                ? auth0User.nickname
                : auth0User.email.split("@")[0],
          });
        } catch (error) {
          if (!isEmailAlreadyRegistered(error)) {
            throw error;
          }
        }

        const signInResult = await create<{
          user?: UserProfile;
          requiresTwoFA?: boolean;
          pendingId?: string;
        }>("/api/v2/auth/sign-in", {});

        if (signInResult?.requiresTwoFA && signInResult.pendingId) {
          localStorage.setItem("pendingId", signInResult.pendingId);
          navigate("/login/2FA-auth");
          return { requiresTwoFA: true as const };
        }

        return { requiresTwoFA: false as const };
      };

      let userData = await fetchUserFromAPI(auth0Token, {
        allowUnauthenticated: true,
      });

      if (!userData) {
        const signInState = await ensureBackendUser();
        if (signInState.requiresTwoFA) {
          return;
        }
        userData = await fetchUserFromAPI(auth0Token);
      }

      if (userData) {
        if (userData.isBlocked === true) {
          clearAuthData();
          sessionStorage.clear();
          setAuthorization(null);
          setUserProfile(null);
          setSettings(null);
          navigate("/account/blocked", { replace: true });
          return;
        }

        const session = {
          aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
          email: userData.email,
          emailVerified: userData.emailVerified,
          name: `${userData.personal_info?.name || ""} ${userData.personal_info?.lastname || ""}`.trim(),
          updatedAt: (userData as Record<string, unknown>).updatedAt,
          sub: userData.authId,
        };

        setAuthData({
          token: auth0Token,
          userId: userData.authId,
          email: userData.email,
          session,
          accountId: userData.accountId,
        });

        setUserProfile(userData);
        await fetchUserSettings(userData);

        if (!userData.emailVerified) {
          navigate("/onboarding/verify-email");
        } else if (!userData.profile_completed) {
          const storedEmails = JSON.parse(
            localStorage.getItem("verifiedEmails") || "[]"
          ) as string[];
          const emailVerifiedFromStorage = storedEmails.includes(
            userData.email
          );
          if (emailVerifiedFromStorage) {
            navigate("/complete-profile");
          } else {
            navigate("/onboarding/welcome");
          }
        } else {
          await ensurePersonalWalletOnLogin();
          navigate(consumePostAuthRedirect(), { replace: true });
        }
      }
    } catch (error) {
      console.error("Social auth callback error:", error);
      socialAuthProcessedRef.current = false;
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    getAccessTokenSilently,
    fetchUserSettings,
    fetchUserFromAPI,
    navigate,
  ]);

  useEffect(() => {
    const pendingId = localStorage.getItem("pendingId");
    if (pendingId) {
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      if (initialLoadRef.current || isLoggingOut) return;
      initialLoadRef.current = true;

      try {
        setLoading(true);

        const backendToken = getAuthToken();
        const tokenValidity = isTokenValid(backendToken);
        if (backendToken && tokenValidity) {
          setAuthorization(backendToken);
          try {
            const userData = await getUser(backendToken);
            if (userData) {
              if (userData.isBlocked === true) {
                if (!isLoggingOut) {
                  clearAuthData();
                  sessionStorage.clear();
                  setAuthorization(null);
                  setUserProfile(null);
                  setSettings(null);
                  navigate("/account/blocked", { replace: true });
                }
                return;
              }

              await fetchUserSettings(userData, backendToken).catch(
                console.error
              );
              setLoading(false);
            }
          } catch (error) {
            console.error("Failed to get user with backend token:", error);
            clearAuthData();
            sessionStorage.clear();
            setAuthorization(null);
            setUserProfile(null);
            setSettings(null);
          }
        }
        if (!tokenValidity) {
          clearAuthData();
          sessionStorage.clear();
          setAuthorization(null);
          setUserProfile(null);
          setSettings(null);
        }

        if (auth0IsAuthenticated && auth0User && !backendToken) {
          await handleSocialAuthCallback();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!auth0IsLoading && !isLoggingOut) {
      initializeAuth();
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    auth0IsLoading,
    getUser,
    fetchUserSettings,
    handleSocialAuthCallback,
    isLoggingOut,
    navigate,
  ]);

  const value = useMemo(
    (): AuthContextValue => ({
      isAuthenticated,
      userProfile,
      user: userProfile,
      settings,
      loading,
      isLoggingOut,

      signInUser,
      refreshUser,
      handleLogout,
      logout: handleLogout,

      updateProfile,
      updateSettings,
      fetchUserSettings,
      getUser,
      getAccountDetails: refreshUser,

      currentUser: userProfile,
    }),
    [
      isAuthenticated,
      userProfile,
      settings,
      loading,
      isLoggingOut,
      signInUser,
      refreshUser,
      handleLogout,
      updateProfile,
      updateSettings,
      fetchUserSettings,
      getUser,
    ]
  );

  if (auth0IsLoading || loading || isLoggingOut) {
    return <SpinningLoader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useAuthNew = useAuth;

export const useIsTwoFactorAuthEnabled = (
  businessIdOverride?: string | null
): boolean => {
  const { user, settings } = useAuth();
  const scope = useWalletScope();
  const { activeBusinessId } = useActiveBusiness();

  const overrideTrimmed =
    typeof businessIdOverride === "string" && businessIdOverride.trim()
      ? businessIdOverride.trim()
      : null;
  const scopeTrimmed =
    typeof scope.businessId === "string" && scope.businessId.trim()
      ? scope.businessId.trim()
      : null;

  const businessId = overrideTrimmed || scopeTrimmed || activeBusinessId;

  const { data: businessMe } = useBusinessMe(businessId);

  if (businessId) {
    return isBusinessTwoFactorEnabled(businessMe);
  }

  if (typeof user?.twoFactorAuth === "boolean") {
    return user.twoFactorAuth;
  }
  return settings?.security?.twoFactorAuth?.enabled ?? false;
};

export const useLocalCurrency = (
  businessIdOverride?: string | null
): DisplayCurrency => {
  const { user, settings } = useAuth();
  const scope = useWalletScope();
  const { activeBusinessId } = useActiveBusiness();

  const overrideTrimmed =
    typeof businessIdOverride === "string" && businessIdOverride.trim()
      ? businessIdOverride.trim()
      : null;
  const scopeTrimmed =
    typeof scope.businessId === "string" && scope.businessId.trim()
      ? scope.businessId.trim()
      : null;
  const businessId = overrideTrimmed || scopeTrimmed || activeBusinessId;

  const { data: businessMe } = useBusinessMe(businessId);

  if (businessId) {
    const fromBusiness = getBusinessMeLocalCurrency(businessMe);
    if (fromBusiness) return fromBusiness;
  }

  const userRecord = (user ?? {}) as Record<string, unknown>;
  const userPreferences = (userRecord.preferences ?? null) as {
    localCurrency?: unknown;
  } | null;
  const rawPersonal =
    settings?.preferences?.localCurrency ?? userPreferences?.localCurrency;

  return normalizeDisplayCurrency(rawPersonal);
};
